import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../context/AuthContext'

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
`

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
`

const Logo = styled.h1`
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;
  margin: 0;

  @media (min-width: 640px) {
    font-size: 1.5rem;
  }
`

const MobileMenuButton = styled.button`
  display: block;
  padding: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.375rem;
  min-height: 44px;
  min-width: 44px;
  
  @media (min-width: 768px) {
    display: none;
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  &:hover {
    background-color: #f9fafb;
  }
`

const NavLinks = styled.div`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    display: flex;
    position: static;
    flex-direction: row;
    padding: 0;
    border: none;
    gap: 2rem;
    box-shadow: none;
  }
`

const StyledNavLink = styled(NavLink)`
  padding: 0.75rem 1rem;
  color: #6b7280;
  text-decoration: none;
  border-radius: 0.375rem;
  transition: all 0.2s;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: #374151;
    background-color: #f9fafb;
  }
  
  &.active {
    color: #2563eb;
    background-color: #eff6ff;
    font-weight: 600;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

const NavIcon = styled.span`
  font-size: 1.1rem;
`

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const UserInfo = styled.div`
  display: none;
  
  @media (min-width: 640px) {
    display: block;
    color: #6b7280;
    font-size: 0.875rem;
  }
`

const LogoutButton = styled.button`
  padding: 0.5rem 1rem;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;
  min-width: 44px;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { user, logout } = useAuth()
  const location = useLocation()

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  // Close mobile menu when route changes
  React.useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  const navItems = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/emails', label: 'Email Management', icon: '📧' },
    { path: '/cohorts', label: 'Cohort Management', icon: '👥' }
  ]

  return (
    <div>
      <Header role="banner">
        <Nav>
          <Logo>Academic Inbox AI</Logo>
          
          <MobileMenuButton 
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </MobileMenuButton>

          <NavLinks isOpen={isMenuOpen} role="navigation" aria-label="Main navigation">
            {navItems.map((item) => (
              <StyledNavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
              >
                <NavIcon>{item.icon}</NavIcon>
                {item.label}
              </StyledNavLink>
            ))}
          </NavLinks>

          <UserSection>
            <UserInfo>
              Welcome, {user?.display_name || user?.email}
            </UserInfo>
            <LogoutButton onClick={logout} aria-label="Log out">
              Logout
            </LogoutButton>
          </UserSection>
        </Nav>
      </Header>
      {children}
    </div>
  )
}

export default Layout
